import styled from '@emotion/styled';
import { RelayType } from '@diritto/common/content/constants';
import { EpisodeStatus } from '@diritto/common/episode/constants';
import { RecentlyReadContentDto } from '@diritto/common/recentlyRead/dtos/recently-read.dto';
import { ContentRelayDto } from '@diritto/common/relay/dtos/content-relay.dtos';
import { EpisodeRelayDto } from '@diritto/common/relay/dtos/episode-relay.dtos';
import { Color } from '@diritto/common/style/color';
import { FontWeight, Typography } from '@diritto/common/style/typography';
import { ZIndex } from 'constants/zIndex';
import { ArrowIcon } from 'icons/ArrowIcon';
import { useReadContinueOrNext } from 'state-hooks/episode/useReadContinueOrNext';
import { getTextLineStyle } from 'styles/common';
import { Link } from 'ui-components/common/Link';
import { ProgressBar } from 'ui-components/common/ProgressBar';
import { ContentBadgeGroup } from 'ui-components/contents/ContentBadgeGroup';
import { ContentCover } from 'ui-components/contents/ContentCover';

const Height = 163;
export const CoverImageWidth = 110;
export const CoverImageRatio = 1;
export const SectionRecentlyReadContentItem = ({
  recentlyReadContent,
  contents,
  episodes,
  onClick,
}: {
  recentlyReadContent: RecentlyReadContentDto;
  contents: Record<number, ContentRelayDto>;
  episodes: Record<number, EpisodeRelayDto>;
  onClick: () => void;
}) => {
  const content = contents[recentlyReadContent.contentId];
  const episode = episodes[recentlyReadContent.episodeId];
  const readContinueOrNext = useReadContinueOrNext(content, episode, recentlyReadContent);

  if (!content || content.relayType !== RelayType.Public) {
    return null;
  }

  if (!episode || episode.status !== EpisodeStatus.Public) {
    return null;
  }

  if (!readContinueOrNext) {
    return null;
  }

  return (
    <StyledContentWrapper key={recentlyReadContent.id} onClick={onClick}>
      <StyledLink href={readContinueOrNext.link}>
        <ContentCover content={content} size={CoverImageWidth} ratio={CoverImageRatio}>
          <ContentBadgeGroup content={content} />
        </ContentCover>
        {!readContinueOrNext.position && (
          <StyledProgressBar
            color={Color.Primary500}
            backgroundColor={Color.Gray500}
            progress={recentlyReadContent.position}
          />
        )}
      </StyledLink>
      <StyledContentMetaContainer>
        <Link href={readContinueOrNext.link}>
          <StyledTitle>{content.title}</StyledTitle>
          <StyledNextEpisode>
            {readContinueOrNext.buttonText}
            <ArrowIcon className="size-[12px]" color={Color.Gray400} direction="right" />
          </StyledNextEpisode>
        </Link>
      </StyledContentMetaContainer>
    </StyledContentWrapper>
  );
};

const StyledContentWrapper = styled.div`
  width: ${CoverImageWidth}px;
  height: ${Height}px;
`;

const StyledContentMetaContainer = styled.div`
  margin-top: 10px;
`;

const StyledTitle = styled.p`
  font-weight: ${FontWeight.Bold};
  ${Typography.XXSmall};
  ${getTextLineStyle(1)};
  margin-bottom: 4px;
`;

const StyledNextEpisode = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;

  font-weight: ${FontWeight.Medium};
  ${Typography.XXXSmall};
  color: ${Color.Gray700};
`;

const StyledLink = styled(Link)`
  display: block;
  position: relative;
`;

const StyledProgressBar = styled(ProgressBar)`
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 4px;
  z-index: ${ZIndex.HomeProgressBar};
  border-radius: 0 0 4px 4px;
  overflow: hidden;
`;
